import { moveToFulfilled, moveToRejected, remove, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  ExtractSuggestionsAction,
  ExtractSuggestionsPendingAction,
} from '../../domains/publisherSuggestions/types';
import type { PublishersRemoveAction } from '../../domains/publishers/closeActions.types';
import type { TopicCreateFulfilledAction } from '../../domains/topics/actions.types';

import type { ExtractedSuggestedTopic, PublisherSuggestions, SuggestionTopicTypes } from './types';

const initialState: PublisherSuggestions = {
  fulfilled: { byId: {} },
  rejected: { byId: {} },
  pending: { byId: {} },
};

const suggestedTopicCreated = (
  state: PublisherSuggestions,
  action: TopicCreateFulfilledAction
): PublisherSuggestions => {
  const { publisherId = '', recommendationId, id } = action.payload;
  const publisherSuggestedTopics = state.fulfilled.byId[publisherId];

  if (!publisherId || !publisherSuggestedTopics) {
    return state;
  }

  const topicsWithChanges = state.fulfilled.byId[publisherId].topics.map((topic: ExtractedSuggestedTopic) => {
    if (topic.recommendationId === recommendationId) {
      return {
        ...topic,
        __typename: 'ExtractedExistingTopicSuggestion' as SuggestionTopicTypes,
        id,
      };
    }

    return topic;
  });

  return update(state, publisherId, {
    topics: topicsWithChanges,
  });
};

const updatePending = (state: PublisherSuggestions, payload: ExtractSuggestionsPendingAction['payload']) => ({
  ...state,
  pending: {
    byId: {
      ...state.pending.byId,
      [payload.id]: true,
    },
  },
});

export type PublisherSuggestionsAction = ExtractSuggestionsAction | TopicCreateFulfilledAction | PublishersRemoveAction;

const reducer: Reducer<PublisherSuggestions, PublisherSuggestionsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'PUBLISHER.EXTRACT_SUGGESTIONS_PENDING':
      return updatePending(state, action.payload);
    case 'PUBLISHER.EXTRACT_SUGGESTIONS_FULFILLED':
      return moveToFulfilled(state, {
        [action.payload.id]: {
          id: action.payload.id,
          topics: action.payload.entities.suggestedTopics,
          groups: action.payload.entities.suggestedGroups,
        },
      });
    case 'PUBLISHER.EXTRACT_SUGGESTIONS_REJECTED':
      return moveToRejected(state, [action.payload.id], action.payload.error);
    case 'TOPIC.CREATE_FULFILLED':
      return suggestedTopicCreated(state, action);
    case 'PUBLISHER.REMOVE':
      return remove(state, [action.payload.id]);
    default:
      return state;
  }
};

export default reducer;
